// The game's series of puzzles, the one source the banner's "Puzzle N of M",
// the card's dots and the stage panels' "Blunders and misses" rows agree on:
// the reader's blunders and misses by the review's own rule (`GameScan.kind`,
// the same call the rows make), in ply order, each of which `startForMove`
// can set up as a puzzle whether or not the finder kept it in the stored
// list. The stored list is not the series: it is read, not generated, when a
// game opens, so it may be empty while the rows are not. Pure, tested.
import type { Color, Move } from '@chess/api-model';
import type { Attempts } from './files.ts';

/** What the series reads of the review's scan: the `GameScan` of `@chess/api-review` satisfies it. */
export interface SeriesScan {
  judgement(ply: number, move: Move | undefined): unknown;
  /** Blunder, miss or neither for the move at a 1-based ply, played by `mover`. */
  kind(ply: number, move: Move | undefined, mover: Color): 'blunder' | 'miss' | undefined;
}

/** The side that played the move at a 1-based ply: odd plies are White's. */
const moverOf = (ply: number): Color => (ply % 2 === 1 ? 'white' : 'black');

/**
 * The 0-based plies (the position the reader was to move in, as `Puzzle.ply`
 * has it) of the reader's blunders and misses, in order. Empty without a
 * scan or a reader.
 */
export function seriesOf(scan: SeriesScan | undefined, moves: readonly Move[], reader: Color | undefined): number[] {
  const out: number[] = [];
  if (!scan || !reader) return out;
  for (let ply = 1; ply <= moves.length; ply++) {
    if (moverOf(ply) !== reader) continue;
    const move = moves[ply - 1];
    if (!scan.judgement(ply, move) || !scan.kind(ply, move, reader)) continue;
    out.push(ply - 1);
  }
  return out;
}

/** The series with the puzzle on the board in it, even one the rule would not list (a stored tactic the rules no longer call a miss). */
export function seriesWith(series: readonly number[], currentPly: number): number[] {
  return series.includes(currentPly) ? [...series] : [...series, currentPly].sort((a, b) => a - b);
}

/** The reader's record on the puzzle at a 0-based ply, tactic first, as `statusOf` reads it. */
export function attemptAt(attempts: Attempts['attempts'], ply: number): 'solved' | 'failed' | undefined {
  return (attempts[`tactic:${ply}`] ?? attempts[`mistake:${ply}`])?.status;
}
